/**
 * One resolver for "which stocker dock does this transfer use".
 *
 * The OHT handoff point (`topology.getStockerPort3DPosition`) and the stocker
 * kit's dock bank (`StockerLoadPortBank`) used to resolve a simulation
 * `transferPortId` independently, with different fallbacks: the OHT dropped back
 * to the crane axis while the kit dropped back to its leftmost dock. Whenever
 * the sim id did not literally equal a layout port id the two landed on
 * different X, so the FOUP was handed off away from the vehicle.
 */

/** Minimal shape shared by layout `Port` and the kit's `LayoutStockerPortRef`. */
export type StockerDockRef = {
  id?: string;
  name?: string;
  rx?: number;
  ry?: number;
  portIndex?: number;
};

/**
 * The 3D stocker model's shuttle/crane sits this fraction of the width off the
 * model's geometric centre along local X.
 */
export const STOCKER_CRANE_X_RATIO = 0.0435156529;

/** Docks hang this fraction of the depth off the stocker centre, on the tray face. */
export const STOCKER_TRAY_HANG_RATIO = 0.75;

/**
 * Tray top as a fraction of stocker height — near the floor so load ports sit at
 * the bottom (compact dock), not a mid-body tower. The OHT hoist descends to
 * exactly this line, so it lives beside the other dock geometry rather than in
 * the renderer that draws it.
 */
export const STOCKER_IO_TRAY_HEIGHT_RATIO = 0.28;

/**
 * Where a tool presents its FOUP to the vehicle, as a fraction of kit depth
 * from the kit centre. Must equal the `ohtHandoff` plane in
 * `getProcessEquipmentPortPoses` (asserted by its tests) — the rail is snapped
 * onto this line so the hoist descends straight into the dock.
 */
export const EQUIPMENT_DOCK_HANG_RATIO = 0.56;

/**
 * FOUP rests this fraction of the nest depth inboard from the OHT handoff
 * line (toward the shutter / chamber, away from the rail).
 */
export const EQUIPMENT_FOUP_SEAT_BACK_INSET = 0.40;

/** Docks left→right, so index 0 is the same dock everywhere. */
export function sortStockerDocks(ports: readonly StockerDockRef[]): StockerDockRef[] {
  return [...ports].sort((a, b) => {
    const ax = typeof a.rx === 'number' ? a.rx : 0.5;
    const bx = typeof b.rx === 'number' ? b.rx : 0.5;
    if (Math.abs(ax - bx) > 1e-6) return ax - bx;
    return (a.portIndex ?? 0) - (b.portIndex ?? 0);
  });
}

/** Trailing dock number in `ST-1-LP-2`, `SL-2`, `LP-2`, `…-PORT-2`. */
function dockNumber(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /(\d+)\s*$/.exec(value);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * Index of the dock a simulation `transferPortId` refers to, within the
 * left→right sorted dock list. `null` when nothing plausibly matches, so the
 * caller can decide whether to fall back or skip.
 *
 * Ids are matched first, then port names, then the trailing dock number — the
 * backend does not always echo the layout's port ids verbatim.
 */
export function resolveStockerDockIndex(
  ports: readonly StockerDockRef[],
  transferPortId: string | null | undefined
): number | null {
  if (!transferPortId || ports.length === 0) return null;
  const sorted = sortStockerDocks(ports);

  const byId = sorted.findIndex((p) => p.id === transferPortId);
  if (byId >= 0) return byId;

  const byName = sorted.findIndex((p) => !!p.name && p.name === transferPortId);
  if (byName >= 0) return byName;

  const dock = dockNumber(transferPortId);
  if (dock === null) return null;

  const byPortIndex = sorted.findIndex((p) => p.portIndex === dock);
  if (byPortIndex >= 0) return byPortIndex;

  const byIdSuffix = sorted.findIndex((p) => dockNumber(p.id) === dock);
  if (byIdSuffix >= 0) return byIdSuffix;

  const byNameSuffix = sorted.findIndex((p) => dockNumber(p.name) === dock);
  if (byNameSuffix >= 0) return byNameSuffix;

  return null;
}

/**
 * Dock index to actually render / aim at. Unresolvable ids collapse onto dock 0
 * — the same choice on both sides, which is what keeps them aligned.
 */
export function resolveStockerDockIndexOrFirst(
  ports: readonly StockerDockRef[],
  transferPortId: string | null | undefined,
  dockCount: number
): number {
  const suffixDock = dockNumber(transferPortId);
  const index =
    resolveStockerDockIndex(ports, transferPortId) ??
    (suffixDock !== null ? suffixDock - 1 : 0);
  return Math.max(0, Math.min(index, Math.max(0, dockCount - 1)));
}

/** Bay SKU → dock count. All docks sit on the OHT tray face. */
export function resolveStockerDockCount(opts: {
  layoutPortCount: number;
  outsideStocker?: boolean;
}): number {
  const n = Math.max(0, opts.layoutPortCount);
  if (opts.outsideStocker) return Math.max(4, n);
  return Math.min(4, Math.max(2, n));
}

/** Even X positions across the tray face, centred on the crane axis. */
export function spreadStockerDockX(
  width: number,
  count: number,
  originX: number
): number[] {
  if (count <= 0) return [];
  if (count === 1) return [originX];
  const span = width * 0.55;
  const step = span / (count - 1);
  const start = originX - span / 2;
  return Array.from({ length: count }, (_, i) => start + i * step);
}

/**
 * X offset from the stocker centre for every dock, left→right.
 *
 * Both the kit's dock meshes and the OHT's handoff target read this, so a
 * stocker with no stored ports still lines its vehicle up with the dock it
 * draws instead of parking on the bare crane axis.
 */
export function resolveStockerDockOffsetsX(
  width: number,
  ports: readonly StockerDockRef[],
  opts: { outsideStocker?: boolean } = {}
): number[] {
  const sorted = sortStockerDocks(ports);
  const count = resolveStockerDockCount({
    layoutPortCount: sorted.length || 2,
    outsideStocker: opts.outsideStocker,
  });
  const craneX = width * STOCKER_CRANE_X_RATIO;
  if (sorted.length === 0) return spreadStockerDockX(width, count, craneX);

  return Array.from({ length: count }, (_, i) => {
    const port = sorted[Math.min(i, sorted.length - 1)];
    const rx = typeof port?.rx === 'number' ? port.rx : 0.5;
    return (rx - 0.5) * width;
  });
}
